import type { Phase } from "@logic/lib/design"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import { selectDefaultInstallArea } from "~/containers/Proposal/state/designs/installAreas"
import type { Interlingual } from "~/i18n/types"

import { areaIndex } from "../elements/areas"
import { edgeIndex } from "../elements/edges"
import { nodeIndex } from "../elements/nodes"
import { panelIndex, panelSelectionState } from "../elements/panels"
import type { TransactionOperation } from "../elements/transactions"
import { changePanelFillState } from "../elements/transactions/changePanelFillState"
import { computeFaces } from "../elements/transactions/computeFaces"
import { deleteAllInvalidPanels } from "../elements/transactions/deleteAllInvalidPanels"
import { removeDeadEnds } from "../elements/transactions/removeDeadEnds"
import { resetPanelSnapToggle } from "../elements/transactions/resetPanelSnapToggle"
import { resetAreaIndex } from "../elements/transactions/resetRoofAttributes"
import { resetSelection } from "../elements/transactions/resetSelection"
import { revisitOutline } from "../elements/transactions/revisitOutline"
import { tryToAutoPlacePanelsOnTransition } from "../elements/transactions/tryToAutoPlacePanelsOnTransition"

const resetAllPhases: TransactionOperation<undefined, void> = (transactors) => {
  const { set } = transactors
  set(areaIndex, [])
  set(panelIndex, [])
  set(edgeIndex, [])
  set(nodeIndex, [])
  set(panelSelectionState, new Set())
}

// Defer the imported binding read until the phase transition runs. This module
// and installAreas are in a legacy circular-import cluster, so reading the
// binding while the production chunk initializes can hit the TDZ.
const selectDefaultInstallAreaOnEntry: TransactionOperation = (transactors) =>
  selectDefaultInstallArea(transactors)

export const useResetAllPhases: () => () => void = () =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => resetAllPhases(transactors),
  )

export const entryOperationsFor: Record<
  Phase,
  ReadonlyArray<TransactionOperation>
> = {
  "Image Selection": [],
  "Outline": [computeFaces],
  "Edge Designation": [removeDeadEnds, computeFaces],
  "Roof Attributes": [selectDefaultInstallAreaOnEntry],
  "Panel Placement": [
    revisitOutline,
    tryToAutoPlacePanelsOnTransition,
    resetPanelSnapToggle,
  ],
  "Panel Activation": [
    deleteAllInvalidPanels,
    changePanelFillState,
    resetSelection,
  ],
}

export const regressionOperationsFor: Record<
  Phase,
  { operation: TransactionOperation; desc: Interlingual | null }
> = {
  "Image Selection": {
    operation: () => {},
    desc: null,
  },
  "Outline": {
    operation: () => {},
    desc: null,
  },
  "Edge Designation": {
    operation: resetAreaIndex,
    desc: `regressionWarning_EdgeDesignation`,
  },
  "Roof Attributes": { operation: () => {}, desc: null },
  "Panel Placement": {
    operation: () => {},
    desc: null,
  },
  "Panel Activation": {
    operation: () => {},
    desc: null,
  },
}
